import React, { useCallback, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { Box } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import ProgressFilters from 'components/progress/ProgressFilters';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { progressListFields } from 'common/constants/query-fields/progress';
import routes from 'common/routes';
import getProgressColumns from 'common/table/columns/progress';
import getProgressData from 'common/table/data/progress';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import AppBarContext from 'context/AppBarContext';
import progressSlice from 'store/modules/progress';

function ProgressList() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['buttons', 'labels', 'routes', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const pageTitle = t('progress.list', { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const filters = useSelector((state) => state.progress.filters);
  const pagination = useSelector((state) => state.progress.pagination);
  const paginatorInfo = useSelector((state) => state.progress.paginatorInfo);
  const progress = useSelector((state) => state.progress.data.progress);
  const refresh = useSelector((state) => state.progress.refresh);
  const status = useSelector((state) => state.progress.status);

  const listProgress = useCallback(() => {
    dispatch(progressSlice.actions.list({ fields: progressListFields, filters, pagination }));
  }, [dispatch, filters, pagination]);

  useEffect(() => {
    listProgress();
  }, [listProgress, refresh]);

  useEffect(() => {
    setAppBarButton(
      <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createProgress.path} />
    );
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const tableData = getProgressData(progress, dateLocale);
  const columns = getProgressColumns({ data: tableData, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(progressSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(progressSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(progressSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    onChangePage,
    page: pagination.page - 1,
    rowsPerPage: pagination.first,
    textLabels: tableLocalization(pageTitle.toLowerCase(), t)
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <ProgressFilters />
      <Box
        sx={{
          position: 'relative'
        }}>
        <LoadingBlock
          emptyBlockProps={{ message: emptyMessage }}
          isValid={progress.length > 0}
          skeletonProps={{ rows: 5 }}
          status={status}
          type="table">
          <MUIDataTable className="progress" columns={columns} data={tableData} options={options} />
        </LoadingBlock>
      </Box>
    </>
  );
}

export default ProgressList;
